import { useQuery } from "@tanstack/react-query";
import { Command as CommandPrimitive } from "cmdk";
import {
  ArrowLeft,
  ClipboardList,
  Copy,
  History,
  Moon,
  Search,
  SquarePen,
  Sun,
  Tv,
  User,
  UserCheck,
  Users,
} from "lucide-react";
import { useTheme } from "next-themes";
import {
  createContext,
  type KeyboardEvent,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { api } from "../api";
import { useSession } from "../auth";
import { useDebouncedValue } from "../hooks/use-debounced-value";
import { useIsMobile } from "../hooks/use-mobile";
import {
  IS_MAC,
  isCommandMenuShortcut,
  matchesLabel,
  type RecentItem,
  recentAllowed,
  rememberRecent,
  searchPlaceholder,
} from "../lib/command-menu";
import { initials } from "../lib/format";
import { backgroundLocationOf } from "../lib/issue-modal";
import { isIssueLookup } from "../lib/issue-search";
import { personLabel } from "../lib/people";
import { useIsMember, useMe } from "../lib/permissions";
import { STATUS_TONE, statusLabel } from "../lib/status";
import { useLocalStorage } from "../lib/use-local-storage";
import { useFactory } from "../store";
import type { SearchResults } from "../types";
import { StatusDisc, TypeIcon, useIssuesMeta } from "./issues/meta";
import { type NavItem, NOTIFICATIONS, useRoleNav } from "./nav";
import { ReleaseKindIcon, ReleaseStatusBadge } from "./releases/release-meta";
import { StatusDot } from "./status-badge";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { CommandGroup, CommandItem, CommandList, CommandSeparator, CommandShortcut } from "./ui/command";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { Kbd } from "./ui/kbd";
import { Spinner } from "./ui/spinner";

const SEARCH_DEBOUNCE_MS = 150;
/** The API searches the first 200 characters. */
const MAX_QUERY = 200;

type Results = SearchResults;
type Person = Results["people"][number];

/** One row the menu can open: its cmdk value and what Enter does. */
interface Entry {
  value: string;
  run: () => void;
}

interface Action {
  id: string;
  label: string;
  icon: ReactNode;
  shortcut?: string;
  run: () => void;
}

interface PersonEntry extends Action {
  detail?: string;
}

interface CommandMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const CommandMenuContext = createContext<CommandMenuContextValue | undefined>(undefined);

/** Owns the global search: Cmd-K (Ctrl-K) toggles it from anywhere, even while typing. */
export function CommandMenuProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (!isCommandMenuShortcut(event) || event.repeat) return;
      event.preventDefault();
      setOpen((current) => !current);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const value = useMemo(() => ({ open, setOpen }), [open]);
  return (
    <CommandMenuContext.Provider value={value}>
      {children}
      <CommandMenu open={open} onOpenChange={setOpen} />
    </CommandMenuContext.Provider>
  );
}

export function useCommandMenu(): CommandMenuContextValue {
  const value = useContext(CommandMenuContext);
  if (!value) throw new Error("useCommandMenu needs a CommandMenuProvider");
  return value;
}

/** The keys that open search on this platform. */
export function SearchShortcut({ className }: { className?: string }) {
  return <Kbd className={className}>{IS_MAC ? "⌘K" : "Ctrl K"}</Kbd>;
}

function CommandMenu({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const session = useSession();
  const role = useMe().data?.role;
  const member = useIsMember();
  const nav = useRoleNav();
  const meta = useIssuesMeta().data;
  const { agentById } = useFactory();
  const { resolvedTheme, setTheme } = useTheme();

  const [query, setQuery] = useState("");
  // A person opens a page of their own: their issues, teams, and email. Back restores the query.
  const [person, setPerson] = useState<{ hit: Person; query: string } | null>(null);
  const [selected, setSelected] = useState("");
  // Enter pressed before the results for the typed text arrived. It opens the top row once they do.
  const [pendingEnter, setPendingEnter] = useState(false);
  const [recents, setRecents] = useLocalStorage<RecentItem[]>(`skynet.search.recent.${session.email}`, []);
  // Queries whose results this opening of the menu has shown. Only those stay on screen while the
  // next results load, so a reopened menu never paints the last session's hits under new text.
  const shown = useRef(new Set<string>());

  useEffect(() => {
    if (open) return;
    setQuery("");
    setPerson(null);
    setPendingEnter(false);
    shown.current = new Set();
  }, [open]);

  const trimmed = query.trim();
  const debounced = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);
  const search = useQuery({
    queryKey: ["search", debounced],
    queryFn: ({ signal }) => api.search(debounced, signal),
    enabled: open && !person && debounced.length > 0,
    placeholderData: (previous) => (previous && shown.current.has(previous.query) ? previous : undefined),
    staleTime: 15_000,
  });
  const results = trimmed && !person ? search.data : undefined;
  const fresh = !trimmed || person !== null || results?.query === trimmed;
  useEffect(() => {
    if (results && fresh) shown.current.add(results.query);
  }, [results, fresh]);
  const searching = Boolean(trimmed) && !person && (search.isFetching || (!fresh && !search.isError));

  const statusByKey = useMemo(() => new Map(meta?.statuses.map((s) => [s.key, s])), [meta]);
  const typeByKey = useMemo(() => new Map(meta?.types.map((t) => [t.key, t])), [meta]);
  const pages = useMemo<NavItem[]>(() => [NOTIFICATIONS, ...nav.flatMap((group) => group.items)], [nav]);

  const close = () => onOpenChange(false);
  const remember = (item: RecentItem) => setRecents((list) => rememberRecent(list, item));
  const go = (to: string, recent?: RecentItem) => {
    if (recent) remember(recent);
    close();
    navigate(to);
  };
  const run = (action: () => void) => {
    close();
    action();
  };

  // Over a list an issue opens as the modal, like clicking its row. On an issue page it replaces the page.
  const openIssue = (issue: { id: string; identifier: string; title: string }) => {
    remember({
      kind: "issue",
      id: issue.id,
      key: issue.identifier,
      title: issue.title,
      to: `/issues/${issue.identifier}`,
    });
    close();
    const background =
      backgroundLocationOf(location.state) ??
      (location.pathname.startsWith("/issues/") ? undefined : location);
    navigate(`/issues/${issue.identifier}`, {
      state: !isMobile && background ? { backgroundLocation: background } : undefined,
    });
  };
  const openTask = (task: Results["tasks"][number]) =>
    go(`/tasks/${task.id}`, {
      kind: "task",
      id: task.id,
      key: task.issueIdentifier ?? undefined,
      title: task.title,
      to: `/tasks/${task.id}`,
    });
  const openTeam = (team: { id: string; key: string; name: string }) =>
    go(`/teams/${team.key}`, {
      kind: "team",
      id: team.id,
      key: team.key,
      title: team.name,
      to: `/teams/${team.key}`,
    });
  const openPerson = (hit: Person) => {
    setPerson({ hit, query });
    setQuery("");
  };
  const openLabel = (label: Results["labels"][number]) =>
    go(`/issues?${new URLSearchParams({ tab: "all", label: label.name })}`);
  const openRelease = (release: Results["releases"][number]) =>
    go(`/releases/${release.id}`, {
      kind: "release",
      id: release.id,
      key: release.tag ?? undefined,
      title: release.name,
      to: `/releases/${release.id}`,
    });
  const openRecent = (item: RecentItem) => {
    if (item.kind === "issue" && item.key)
      openIssue({ id: item.id, identifier: item.key, title: item.title });
    else go(item.to, item);
  };
  const searchAllIssues = () => go(`/issues?${new URLSearchParams({ tab: "all", q: trimmed })}`);
  const back = () => {
    setQuery(person?.query ?? "");
    setPerson(null);
  };

  const actions: Action[] = [];
  if (member) {
    actions.push({
      id: "new-issue",
      label: "New issue",
      icon: <SquarePen />,
      shortcut: "C",
      run: () => navigate("/issues/new"),
    });
  }
  const dark = resolvedTheme === "dark";
  actions.push(
    {
      id: "theme",
      label: dark ? "Switch to light theme" : "Switch to dark theme",
      icon: dark ? <Sun /> : <Moon />,
      run: () => setTheme(dark ? "light" : "dark"),
    },
    {
      id: "tv",
      label: "TV mode",
      icon: <Tv />,
      run: () => navigate("/tv", { state: { from: location.pathname + location.search } }),
    },
  );

  const visibleRecents = recents.filter((item) => recentAllowed(item, role));
  const matchedPages = trimmed ? pages.filter((page) => matchesLabel(page.label, trimmed)) : pages;
  const matchedActions = trimmed ? actions.filter((action) => matchesLabel(action.label, trimmed)) : actions;
  const personEntries = person
    ? entriesFor(person.hit, go, close).filter((e) => matchesLabel(e.label, trimmed))
    : [];
  const found = results
    ? results.issues.length +
      results.tasks.length +
      results.teams.length +
      results.people.length +
      results.labels.length +
      results.releases.length
    : 0;
  const nothingMatches =
    results !== undefined && fresh && found === 0 && matchedPages.length === 0 && matchedActions.length === 0;
  const lookup = isIssueLookup(trimmed);
  const showSearchAll = Boolean(trimmed) && (results !== undefined || !searching);

  // The top row in render order. Keep in step with the groups below.
  const pageEntry = matchedPages[0] && {
    value: `page:${matchedPages[0].to}`,
    run: () => go(matchedPages[0]!.to),
  };
  const issueEntry = results?.issues[0] && {
    value: `issue:${results.issues[0].id}`,
    run: () => openIssue(results.issues[0]!),
  };
  const first: Entry | undefined = person
    ? personEntries[0] && { value: `person-action:${personEntries[0].id}`, run: personEntries[0].run }
    : !trimmed
      ? ((visibleRecents[0] && {
          value: recentValue(visibleRecents[0]),
          run: () => openRecent(visibleRecents[0]!),
        }) ??
        pageEntry ??
        (matchedActions[0] && {
          value: `action:${matchedActions[0].id}`,
          run: () => run(matchedActions[0]!.run),
        }))
      : ((lookup ? (issueEntry ?? pageEntry) : (pageEntry ?? issueEntry)) ??
        (results?.tasks[0] && {
          value: `task:${results.tasks[0].id}`,
          run: () => openTask(results.tasks[0]!),
        }) ??
        (results?.teams[0] && {
          value: `team:${results.teams[0].id}`,
          run: () => openTeam(results.teams[0]!),
        }) ??
        (results?.people[0] && {
          value: `person:${results.people[0].id}`,
          run: () => openPerson(results.people[0]!),
        }) ??
        (results?.labels[0] && {
          value: `label:${results.labels[0].id}`,
          run: () => openLabel(results.labels[0]!),
        }) ??
        (results?.releases[0] && {
          value: `release:${results.releases[0].id}`,
          run: () => openRelease(results.releases[0]!),
        }) ??
        (matchedActions[0] && {
          value: `action:${matchedActions[0].id}`,
          run: () => run(matchedActions[0]!.run),
        }) ??
        (showSearchAll ? { value: "search-issues", run: searchAllIssues } : undefined));

  // cmdk keeps a row selected while it stays mounted, so new results would land under a stale
  // highlight. Whenever the view changes, the highlight goes back to the top row.
  const view = `${open}\u0000${person?.hit.id ?? ""}\u0000${trimmed}\u0000${results?.query ?? ""}\u0000${search.isError}`;
  const [seenView, setSeenView] = useState(view);
  if (seenView !== view) {
    setSeenView(view);
    setSelected(first?.value ?? "");
  }

  useEffect(() => {
    if (!pendingEnter) return;
    if (search.isError) setPendingEnter(false);
    else if (fresh) {
      setPendingEnter(false);
      first?.run();
    }
  }, [pendingEnter, fresh, search.isError, first]);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && !fresh && !search.isError) {
      event.preventDefault();
      setPendingEnter(true);
    } else if (person && event.key === "Backspace" && !query) {
      event.preventDefault();
      back();
    }
  };

  const pagesGroup = matchedPages.length > 0 && (
    <CommandGroup heading="Pages">
      {matchedPages.map((page) => (
        <CommandItem key={page.to} value={`page:${page.to}`} onSelect={() => go(page.to)}>
          <page.icon className="text-muted-foreground" />
          {page.label}
        </CommandItem>
      ))}
    </CommandGroup>
  );

  const issuesGroup = results && results.issues.length > 0 && (
    <CommandGroup heading="Issues">
      {results.issues.map((issue) => {
        const status = statusByKey.get(issue.statusKey);
        return (
          <CommandItem key={issue.id} value={`issue:${issue.id}`} onSelect={() => openIssue(issue)}>
            <TypeIcon typeKey={issue.typeKey} color={typeByKey.get(issue.typeKey)?.color} />
            <span className="w-18 shrink-0 font-mono text-xs text-muted-foreground tabular-nums">
              {issue.identifier}
            </span>
            <span className="min-w-0 flex-1 truncate">{issue.title}</span>
            {issue.jiraKey && (
              <Badge variant="outline" className="shrink-0 font-mono font-normal text-muted-foreground">
                Jira {issue.jiraKey}
              </Badge>
            )}
            {issue.archived && (
              <Badge variant="outline" className="font-normal text-muted-foreground">
                Archived
              </Badge>
            )}
            <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground max-sm:hidden">
              <StatusDisc category={status?.category} color={status?.color} />
              {status?.name ?? issue.statusKey}
            </span>
          </CommandItem>
        );
      })}
    </CommandGroup>
  );

  const actionsGroup = matchedActions.length > 0 && (
    <CommandGroup heading="Actions">
      {matchedActions.map((action) => (
        <CommandItem key={action.id} value={`action:${action.id}`} onSelect={() => run(action.run)}>
          <span className="text-muted-foreground [&_svg]:size-4">{action.icon}</span>
          {action.label}
          {action.shortcut && <CommandShortcut>{action.shortcut}</CommandShortcut>}
        </CommandItem>
      ))}
    </CommandGroup>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        sheet={false}
        className="top-[10dvh] translate-y-0 gap-0 overflow-hidden p-0 sm:top-[14dvh] sm:max-w-2xl"
        onEscapeKeyDown={(event) => {
          if (!person) return;
          event.preventDefault();
          back();
        }}
      >
        <DialogTitle className="sr-only">Search Skynet</DialogTitle>
        <DialogDescription className="sr-only">
          Find issues, records, and pages you have access to, then press Enter to open one.
        </DialogDescription>
        <CommandPrimitive
          shouldFilter={false}
          loop
          value={selected}
          onValueChange={setSelected}
          onKeyDown={onKeyDown}
          className="flex flex-col"
        >
          <div className="flex h-12 items-center gap-2 border-b px-3">
            {person ? (
              <button
                type="button"
                onClick={back}
                aria-label="Back to results"
                className="flex shrink-0 items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs font-medium text-foreground outline-hidden ring-ring focus-visible:ring-2"
              >
                <ArrowLeft className="size-3.5" />
                {personLabel(person.hit)}
              </button>
            ) : (
              <Search className="size-4 shrink-0 text-muted-foreground" />
            )}
            <CommandPrimitive.Input
              value={query}
              onValueChange={(value) => {
                setQuery(value);
                setPendingEnter(false);
              }}
              maxLength={MAX_QUERY}
              placeholder={person ? "Choose what to open…" : searchPlaceholder(role)}
              className="h-full min-w-0 flex-1 bg-transparent text-sm outline-hidden placeholder:text-muted-foreground max-md:text-base"
            />
            {searching && <Spinner className="text-muted-foreground" />}
            <Kbd className="max-md:hidden">Esc</Kbd>
          </div>
          <CommandList className="max-h-[min(60dvh,30rem)] scroll-py-2 overflow-y-auto overscroll-contain p-1.5">
            {person ? (
              <CommandGroup heading={personLabel(person.hit)}>
                {personEntries.map((entry) => (
                  <CommandItem key={entry.id} value={`person-action:${entry.id}`} onSelect={entry.run}>
                    <span className="text-muted-foreground [&_svg]:size-4">{entry.icon}</span>
                    <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                    {entry.detail && (
                      <span className="shrink-0 font-mono text-xs text-muted-foreground">{entry.detail}</span>
                    )}
                  </CommandItem>
                ))}
                {personEntries.length === 0 && (
                  <p className="px-2 py-4 text-center text-sm text-muted-foreground">No matches</p>
                )}
              </CommandGroup>
            ) : !trimmed ? (
              <>
                {visibleRecents.length > 0 && (
                  <CommandGroup heading="Recent">
                    {visibleRecents.map((item) => (
                      <CommandItem
                        key={recentValue(item)}
                        value={recentValue(item)}
                        onSelect={() => openRecent(item)}
                      >
                        <History className="text-muted-foreground" />
                        {item.key && (
                          <span className="shrink-0 font-mono text-xs text-muted-foreground">{item.key}</span>
                        )}
                        <span className="min-w-0 flex-1 truncate">{item.title}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {RECENT_KIND[item.kind]}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {pagesGroup}
                {actionsGroup}
              </>
            ) : (
              <>
                {lookup ? issuesGroup : pagesGroup}
                {lookup ? pagesGroup : issuesGroup}
                {results && results.tasks.length > 0 && (
                  <CommandGroup heading="Work orders">
                    {results.tasks.map((task) => (
                      <CommandItem key={task.id} value={`task:${task.id}`} onSelect={() => openTask(task)}>
                        <ClipboardList className="text-muted-foreground" />
                        {task.issueIdentifier && (
                          <span className="w-18 shrink-0 font-mono text-xs text-muted-foreground">
                            {task.issueIdentifier}
                          </span>
                        )}
                        <span className="min-w-0 flex-1 truncate">{task.title}</span>
                        <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground max-sm:hidden">
                          <StatusDot tone={STATUS_TONE[task.status] ?? "neutral"} />
                          {statusLabel(task.status)}
                          {task.currentAgentId && agentById.get(task.currentAgentId) && (
                            <> · {agentById.get(task.currentAgentId)?.name}</>
                          )}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {results && results.teams.length > 0 && (
                  <CommandGroup heading="Teams">
                    {results.teams.map((team) => (
                      <CommandItem key={team.id} value={`team:${team.id}`} onSelect={() => openTeam(team)}>
                        <span
                          className="size-3 shrink-0 rounded-sm"
                          style={{ backgroundColor: team.color }}
                        />
                        <span className="shrink-0">{team.name}</span>
                        <span className="shrink-0 font-mono text-xs text-muted-foreground">{team.key}</span>
                        {team.description && (
                          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                            {team.description}
                          </span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {results && results.people.length > 0 && (
                  <CommandGroup heading="People">
                    {results.people.map((hit) => (
                      <CommandItem key={hit.id} value={`person:${hit.id}`} onSelect={() => openPerson(hit)}>
                        <Avatar className="size-5">
                          <AvatarFallback className="text-[10px] font-medium">
                            {initials(personLabel(hit))}
                          </AvatarFallback>
                        </Avatar>
                        <span className="shrink-0">{personLabel(hit)}</span>
                        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                          {hit.email}
                        </span>
                        {hit.teams.length > 0 && (
                          <span className="shrink-0 text-xs text-muted-foreground max-sm:hidden">
                            {hit.teams.map((team) => team.name).join(", ")}
                          </span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {results && results.labels.length > 0 && (
                  <CommandGroup heading="Labels">
                    {results.labels.map((label) => (
                      <CommandItem
                        key={label.id}
                        value={`label:${label.id}`}
                        onSelect={() => openLabel(label)}
                      >
                        <span
                          className="size-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: label.color }}
                        />
                        <span className="min-w-0 flex-1 truncate">{label.name}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">Issues with this label</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {results && results.releases.length > 0 && (
                  <CommandGroup heading="Releases">
                    {results.releases.map((release) => (
                      <CommandItem
                        key={release.id}
                        value={`release:${release.id}`}
                        onSelect={() => openRelease(release)}
                      >
                        <ReleaseKindIcon kind={release.kind} className="text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate">{release.name}</span>
                        {release.tag && release.tag !== release.name && (
                          <span className="shrink-0 font-mono text-xs text-muted-foreground">
                            {release.tag}
                          </span>
                        )}
                        <ReleaseStatusBadge status={release.status} />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {actionsGroup}
                {showSearchAll && (
                  <>
                    {(found > 0 || matchedPages.length > 0 || matchedActions.length > 0) && (
                      <CommandSeparator />
                    )}
                    <CommandGroup>
                      <CommandItem value="search-issues" onSelect={searchAllIssues}>
                        <Search className="text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate">
                          Search all issues for <span className="font-medium">“{trimmed}”</span>
                        </span>
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </>
            )}
          </CommandList>
          {nothingMatches && !person && (
            <p className="border-t px-4 py-3 text-xs text-muted-foreground">
              Nothing you can access matches “{trimmed}”. Try an issue key like BUG-42, a team key, or a name.
            </p>
          )}
          {search.isError && !person && (
            <p className="border-t px-4 py-3 text-xs text-destructive">
              Search failed. Try again in a moment.
            </p>
          )}
          <div className="flex items-center gap-4 border-t px-3 py-2 text-xs text-muted-foreground max-md:hidden">
            <span className="flex items-center gap-1">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd>
              to move
            </span>
            <span className="flex items-center gap-1">
              <Kbd>↵</Kbd>
              to open
            </span>
            {person && (
              <span className="flex items-center gap-1">
                <Kbd>⌫</Kbd>
                to go back
              </span>
            )}
          </div>
        </CommandPrimitive>
      </DialogContent>
    </Dialog>
  );
}

const RECENT_KIND: Record<RecentItem["kind"], string> = {
  issue: "Issue",
  task: "Work order",
  team: "Team",
  release: "Release",
};

function recentValue(item: RecentItem): string {
  return `recent:${item.kind}:${item.id}`;
}

/** What can be opened for one person. Teams only appear when the role can open team pages. */
function entriesFor(person: Person, go: (to: string) => void, close: () => void): PersonEntry[] {
  const name = personLabel(person);
  const issues = (key: "reviewer" | "reporter") =>
    `/issues?${new URLSearchParams({ tab: "all", [key]: person.id }).toString()}`;
  const entries: PersonEntry[] = [
    {
      id: "reviewer",
      label: `Issues ${name} reviews`,
      icon: <UserCheck />,
      run: () => go(issues("reviewer")),
    },
    { id: "reporter", label: `Issues ${name} reported`, icon: <User />, run: () => go(issues("reporter")) },
    ...person.teams.map((team) => ({
      id: `team:${team.id}`,
      label: `${team.name} team`,
      icon: <Users />,
      detail: team.key,
      run: () => go(`/teams/${team.key}`),
    })),
  ];
  if (person.email.includes("@")) {
    entries.push({
      id: "email",
      label: "Copy email address",
      icon: <Copy />,
      detail: person.email,
      run: () => {
        close();
        void navigator.clipboard.writeText(person.email).then(
          () => toast.success("Email address copied"),
          () => toast.error("Could not copy the email address"),
        );
      },
    });
  }
  return entries;
}
